import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import type {IFriend, IFriendsStore} from "data/stores/friends/friends.store";
import {Bindings} from "data/constants/bindings";
import {RequestState, ModalType, ContestStatus} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";
import {AxiosError} from "axios";
import {ChallengeStatus} from "data/enums/challenge";
import {Empty} from "data/types/global";
import type {IH2hStore} from "data/stores/h2h/h2h.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IRosterStore} from "data/stores/roster/roster.store";

interface IParams {
	friend: IFriend;
}

export interface IFriendPlayRowController extends ViewController<IParams> {
	i18n: ILocalizationStore;
	get friendStatus(): Empty<ChallengeStatus>;

	get isComplete(): boolean;

	get userHasRosters(): boolean;

	onPlay: () => void;
	onRemove: () => void;
	onAccept: () => void;
}

@injectable()
export class FriendPlayRowController implements IFriendPlayRowController {
	@observable _friend?: IFriend;
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.FriendsStore) public readonly _friendsStore: IFriendsStore,
		@inject(Bindings.H2hStore) public readonly _h2hStore: IH2hStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore,
		@inject(Bindings.ContestsStore) public readonly _contestStore: IContestsStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore
	) {
		makeAutoObservable(this);
	}

	get friendId() {
		return this._friend?.friendId;
	}

	get friendStatus() {
		return this._friend?.status;
	}

	get userHasRosters() {
		return this._rosterStore.isRosterFull;
	}

	init(param: IParams) {
		this._friend = param.friend;
	}

	onChange(param: IParams) {
		this.init(param);
	}

	get contestId() {
		return this._contestStore.currentGameContest?.id;
	}

	get isComplete() {
		return this._contestStore.currentGameContest?.status === ContestStatus.Complete;
	}

	onPlay = () => {
		const friendId = this.friendId;
		const contestId = this.contestId;
		this._requestState = RequestState.PENDING;
		if (friendId && contestId) {
			this._h2hStore
				.inviteFriendToPlay(contestId, friendId)
				.then(this.onSuccess)
				.then(() => {
					runInAction(() => {
						if (this._friend) {
							this._friend.isH2HChallenging = true;
						}
					});
				})
				.catch(this.onError);
		}
	};

	onRemove = () => {
		const friendId = this.friendId;
		const friend = this._friend;
		this._modalsStore.showModal(ModalType.CONFIRM, {
			title: this.i18n.t("friends.row.remove_title", "Are you sure?"),
			message: this.i18n.t(
				"friends.row.remove_description",
				`Would you like to remove {{X}}?`,
				{
					X: friend?.displayName || "this friend",
				}
			),
			callback: () => {
				this._requestState = RequestState.PENDING;
				if (friendId) {
					this._friendsStore
						.deleteFriend(friendId)
						.then(this.onSuccess)
						.catch(this.onError);
					this._modalsStore.hideModal();
				}
			},
		});
	};

	onAccept = () => {
		const friend = this._friend;
		this._requestState = RequestState.PENDING;
		if (friend) {
			this._friendsStore.accept(friend).then(this.onSuccess).catch(this.onError);
		}
	};

	private onSuccess = () => {
		runInAction(() => {
			this._requestState = RequestState.SUCCESS;
		});
	};

	private onError = (error: AxiosError) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
		runInAction(() => {
			this._requestState = RequestState.ERROR;
		});
	};
}
